import { useState, useEffect, useEffectEvent } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { ErrorLike } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { DialogContentText } from '@mui/material';

import {
    IModuleType,
    DeleteModuleDocument,
    UpdateLinkModuleDocument,
    ModuleEditDocument,
} from 'graphql/types';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useApolloError } from 'common/hooks/useApolloError';
import { PageTitle } from 'common/components/PageTitle';
import {
    LinkModuleForm,
    ILinkModuleFormProps,
} from 'training/components/forms/LinkModuleForm';
import { IModuleUpdateDrawerProps } from 'training/types';
import { dayHoursToSeconds } from 'common/utils/formatDate';
import { Loader } from 'common/components/Loader';
import { getModuleOffsetFormValue } from 'training/utils/moduleOffset';
import { AlertDialog } from 'common/components/AlertDialog';
import { Button } from 'common/components/Button';
import { getImageId } from 'common/utils/image';

export const LinkModuleUpdateDrawer = ({
    id,
    onClose,
}: IModuleUpdateDrawerProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();

    const [showDeleteAlert, setShowDeleteAlert] = useState<boolean>(false);

    const { data, loading, error } = useQuery(ModuleEditDocument, {
        variables: { id, type: IModuleType.Link },
    });

    const onError = useEffectEvent((error: ErrorLike) => {
        showApolloError(error);
    });

    useEffect(() => {
        if (error) {
            onError(error);
        }
    }, [error]);

    const [updateModule] = useMutation(UpdateLinkModuleDocument);
    const [deleteModule, { loading: loadingDelete }] =
        useMutation(DeleteModuleDocument);

    const module = data?.module;

    if (loading || !module) return <Loader />;

    if (!module || module.__typename !== 'LinkModule') return null;

    const handleSubmit: ILinkModuleFormProps['onSubmit'] = async (values) => {
        const { url, image, offset, ...otherValues } = values;
        const { image: currentImage } = module;

        const moduleValues = {
            ...otherValues,
            // The protocol is removed by the field so we always want to add https:// here.
            url: `https://${url}`,
            imageId: getImageId(currentImage, image),
            offset: {
                seconds: dayHoursToSeconds(offset?.days, offset?.hours),
                type: offset?.type,
            },
        };

        try {
            await updateModule({
                variables: { id, module: moduleValues },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(translate('moduleActionSuccess.link.update'), {
            variant: 'success',
        });

        // Close drawer
        onClose?.();

        return;
    };

    const handleDelete = async () => {
        try {
            await deleteModule({
                variables: {
                    id,
                    type: IModuleType.Link,
                },
                update: (cache) => {
                    cache.evict({ id: `LinkModule:${id}` });
                    cache.gc();
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(translate('moduleActionSuccess.link.delete'), {
            variant: 'success',
        });

        // Close drawer
        onClose?.();

        return;
    };

    const {
        title,
        description,
        url,
        image,
        openOffset,
        moduleGroup: { id: moduleGroupId },
    } = module;

    const initialValues = {
        title,
        description,
        image: image || undefined,
        // Pass url without protocol
        url: url.replace(/(^\w+:|^)\/\//, ''),
        offset: getModuleOffsetFormValue(openOffset),
    };

    return (
        <>
            <PageTitle>{translate('updateLinkModule')}</PageTitle>

            <LinkModuleForm
                edit
                initialValues={initialValues}
                moduleGroupId={moduleGroupId}
                onClose={onClose}
                onDelete={() => setShowDeleteAlert(true)}
                onSubmit={handleSubmit}
            />

            <AlertDialog
                actions={
                    <>
                        <Button
                            autoFocus
                            color="error"
                            loading={loadingDelete}
                            variant="contained"
                            onClick={handleDelete}
                        >
                            {translate('delete')}
                        </Button>
                        <Button
                            disabled={loadingDelete}
                            variant="contained"
                            onClick={() => {
                                setShowDeleteAlert(false);
                            }}
                        >
                            {translate('cancel')}
                        </Button>
                    </>
                }
                open={showDeleteAlert}
                title={translate('deleteLinkModuleMessage.title')}
            >
                <DialogContentText color="text.primary" variant="body2">
                    {translate('deleteLinkModuleMessage.text')}
                </DialogContentText>
            </AlertDialog>
        </>
    );
};
